import React, { useEffect, useState } from "react";
import { KeyRound, ShieldAlert, Loader2, Lock, Ban, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { cn } from "@/lib/utils";

const STATUS_STYLE = {
  CONFIGURED: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  NOT_CONFIGURED: "bg-white/5 text-white/50 border-white/10",
  DISABLED: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  ERROR: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

export default function AdminSecrets() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { key, mode: 'set'|'disable', high_risk }
  const [form, setForm] = useState({ value: "", password: "", totp_code: "", email_otp: "" });
  const [busy, setBusy] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const load = () =>
    api.get("/admin/vault/secrets").then((r) => setData(r.data)).catch(() => toast.error("Failed to load vault")).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openModal = (key, mode, high_risk) => {
    setModal({ key, mode, high_risk });
    setForm({ value: "", password: "", totp_code: "", email_otp: "" });
    setOtpSent(false);
  };
  const closeModal = () => { setModal(null); setForm({ value: "", password: "", totp_code: "", email_otp: "" }); };

  const sendOtp = async () => {
    try {
      const r = await api.post("/admin/vault/otp/request");
      if (r.data.sent) { setOtpSent(true); toast.success("Email OTP sent"); }
      else toast.warning("Email provider not configured yet");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send OTP"); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const mfa = { password: form.password, totp_code: form.totp_code || undefined, email_otp: form.email_otp || undefined };
      if (modal.mode === "disable") {
        await api.post(`/admin/vault/secrets/${modal.key}/disable`, mfa);
        toast.success(`${modal.key} disabled`);
      } else {
        await api.post(`/admin/vault/secrets/${modal.key}`, { ...mfa, value: form.value });
        toast.success(`${modal.key} saved securely`);
      }
      setForm({ value: "", password: "", totp_code: "", email_otp: "" }); // clear client input
      closeModal();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Operation failed");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-emerald-400" /></div>;

  const groups = (data?.secrets || []).reduce((a, s) => { (a[s.category] = a[s.category] || []).push(s); return a; }, {});
  const mfa = data?.mfa || {};

  return (
    <div className="space-y-6" data-testid="admin-secrets-vault">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-white"><KeyRound className="text-emerald-400" /> Secrets Vault</h1>
        <p className="mt-1 text-sm text-slate-500">Super-admin only. Values are encrypted at rest and never displayed. Configuring a secret does <b>not</b> enable any live feature.</p>
      </div>

      <div className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700">
        <ShieldAlert size={16} className="mt-0.5 shrink-0" />
        <span>Every change requires password re-auth{mfa.totp ? " + Google Authenticator" : ""}{mfa.email_otp ? " + Email OTP" : " (Email OTP will be enforced once the email provider is configured)"}. There is no reveal/copy/export.</span>
      </div>

      {Object.entries(groups).map(([cat, list]) => (
        <div key={cat} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-emerald-300/80">{cat}</h3>
          <div className="space-y-2">
            {list.map((s) => (
              <div key={s.key} data-testid={`secret-row-${s.key}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/5 bg-black/20 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 font-mono text-sm text-white">
                    {s.key}
                    {s.high_risk && <span className="rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-bold text-rose-300">HIGH RISK</span>}
                  </div>
                  <div className="text-[11px] text-white/40">{s.purpose} · <span className="text-white/30">{s.required_for}</span></div>
                </div>
                <span className={cn("rounded-full border px-2.5 py-1 text-[10px] font-bold", STATUS_STYLE[s.status] || STATUS_STYLE.NOT_CONFIGURED)} data-testid={`secret-status-${s.key}`}>{s.status}</span>
                <button onClick={() => openModal(s.key, "set", s.high_risk)} data-testid={`secret-configure-${s.key}`}
                  className="flex items-center gap-1 rounded-lg bg-emerald-500/15 px-3 py-1.5 text-[11px] font-bold text-emerald-300 hover:bg-emerald-500/25">
                  {s.status === "CONFIGURED" ? <><RefreshCw size={12} /> Rotate</> : <><Lock size={12} /> Configure</>}
                </button>
                {s.status !== "NOT_CONFIGURED" && (
                  <button onClick={() => openModal(s.key, "disable", s.high_risk)} data-testid={`secret-disable-${s.key}`}
                    className="flex items-center gap-1 rounded-lg bg-white/5 px-2.5 py-1.5 text-[11px] font-bold text-white/60 hover:bg-white/10"><Ban size={12} /></button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={closeModal}>
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0b1512] p-6" onClick={(e) => e.stopPropagation()} data-testid="secret-modal">
            <h3 className="flex items-center gap-2 text-lg font-bold text-white">
              {modal.mode === "disable" ? <><Ban size={18} className="text-amber-400" /> Disable secret</> : <><Lock size={18} className="text-emerald-400" /> Configure secret</>}
            </h3>
            <p className="mt-1 font-mono text-xs text-emerald-300">{modal.key}</p>
            {modal.high_risk && <p className="mt-2 rounded-lg bg-rose-500/10 px-3 py-2 text-[11px] text-rose-300">High-risk secret. Configuring it does not enable the related feature.</p>}

            <div className="mt-4 space-y-3">
              {modal.mode === "set" && (
                <input type="password" autoComplete="new-password" placeholder="Secret value" value={form.value}
                  onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="secret-value-input"
                  className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-emerald-500/50" />
              )}
              <input type="password" autoComplete="off" placeholder="Your password" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="secret-password-input"
                className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-emerald-500/50" />
              {mfa.totp && (
                <input inputMode="numeric" placeholder="Google Authenticator code" value={form.totp_code}
                  onChange={(e) => setForm({ ...form, totp_code: e.target.value })} data-testid="secret-totp-input"
                  className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-emerald-500/50" />
              )}
              {mfa.email_otp && (
                <div className="flex gap-2">
                  <input inputMode="numeric" placeholder="Email OTP" value={form.email_otp}
                    onChange={(e) => setForm({ ...form, email_otp: e.target.value })} data-testid="secret-email-otp-input"
                    className="flex-1 rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-emerald-500/50" />
                  <button onClick={sendOtp} data-testid="secret-send-otp-btn" className="rounded-lg bg-white/10 px-3 py-2 text-[11px] font-bold text-white hover:bg-white/20">{otpSent ? "Resend" : "Send OTP"}</button>
                </div>
              )}
            </div>

            <div className="mt-5 flex gap-2">
              <button onClick={closeModal} className="flex-1 rounded-lg border border-white/10 py-2 text-sm text-white/70 hover:bg-white/5">Cancel</button>
              <button onClick={submit} disabled={busy} data-testid="secret-submit-btn"
                className={cn("flex flex-1 items-center justify-center gap-1 rounded-lg py-2 text-sm font-bold text-white disabled:opacity-50",
                  modal.mode === "disable" ? "bg-amber-600 hover:bg-amber-500" : "bg-emerald-600 hover:bg-emerald-500")}>
                {busy && <Loader2 size={14} className="animate-spin" />} {modal.mode === "disable" ? "Disable" : "Save Securely"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
